import jsPDF from "jspdf";
import { SEEDH } from "@/lib/seedh";
import { formatDate } from "@/lib/date";
import { CAUSE_LABEL } from "@/lib/causes";

/**
 * Certificate PDFs for a single tree or for a whole drive, on A4 portrait.
 *
 * Layout follows the approved design: soft tinted corners behind a rounded
 * double border, logo and registered name at the top, the recipient set large
 * in serif italic, a details card of labelled rows, then a QR on the left and
 * the signatory block on the right.
 *
 * Everything is drawn with jsPDF's vector API plus three small rasters — logo,
 * QR and the signature — each of which degrades gracefully: if one fails to
 * load the certificate is still produced without it, because a missing image
 * must never cost the donor their download.
 */

/* ---------------- palette (from the approved design) ---------------- */
const PAPER = [248, 250, 244];
const SOFT_BLUE = [232, 243, 247];
const SOFT_GREEN = [234, 244, 232];
const BLUE = [7, 94, 137];
const NAVY = [10, 79, 115];
const GREEN = [75, 174, 79];
const DEEP_GREEN = [47, 107, 62];
const BROWN = [155, 99, 45];
const INK = [23, 37, 45];
const MUTED = [104, 119, 125];
const RULE = [217, 229, 219];
const WHITE = [255, 255, 255];

const origin = () =>
    (typeof window !== "undefined" && window.location?.origin) || "https://seedhwelfaresociety.org";

/**
 * QR for the record's URL as a PNG data URI. The library is imported lazily so
 * it only loads when someone actually downloads a certificate.
 */
async function qrDataUrl(text) {
    try {
        const QRCode = (await import("qrcode")).default;
        return await QRCode.toDataURL(text, {
            errorCorrectionLevel: "M",
            margin: 1,
            width: 240,
            color: { dark: "#104028", light: "#F8FAF4" },
        });
    } catch {
        return null;
    }
}

/** Load a same-origin image; resolves to null rather than rejecting. */
function loadImage(src) {
    return new Promise((resolve) => {
        if (typeof window === "undefined") return resolve(null);
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

/**
 * Prepare a signature scan for placing on the certificate. Two things have to
 * happen or it looks wrong:
 *
 *  1. Knock out the paper. A scan sits on white — often with texture and a
 *     shadow — which would print as an obvious rectangle over the tinted stock.
 *     Near-white pixels go transparent and the ink is recoloured to brand navy,
 *     with soft edges kept semi-transparent so the stroke stays smooth.
 *  2. Crop to the ink. A phone photo or screenshot is mostly empty margin (the
 *     supplied one is 339x737 with the signature small in the middle), so
 *     sizing by the raw dimensions would render a tiny sliver. Cropping to the
 *     ink's bounding box means any scan — tall, wide or heavily padded — fills
 *     the signature area properly.
 */
async function loadSignature(src) {
    const img = await loadImage(src);
    if (!img) return null;
    try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, c.width, c.height);
        const px = data.data;

        let minX = c.width;
        let minY = c.height;
        let maxX = -1;
        let maxY = -1;
        for (let i = 0; i < px.length; i += 4) {
            // Respect any transparency the file already has.
            if (px[i + 3] === 0) continue;
            const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
            if (lum > 205) {
                px[i + 3] = 0; // paper → transparent
                continue;
            }
            px[i] = NAVY[0];
            px[i + 1] = NAVY[1];
            px[i + 2] = NAVY[2];
            // Only derive alpha from luminance for a flat scan. A file that
            // already carries partial alpha has been prepared, and rewriting it
            // would harden the antialiased edges of the stroke.
            if (px[i + 3] === 255) {
                px[i + 3] = Math.min(255, Math.round((205 - lum) * 3.2));
            }

            const p = i / 4;
            const x = p % c.width;
            const y = (p - x) / c.width;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
        ctx.putImageData(data, 0, 0);
        if (maxX < 0) return null; // nothing but paper

        // Crop to the ink, with a small breathing margin.
        const pad = Math.round(Math.max(c.width, c.height) * 0.01);
        const cx = Math.max(0, minX - pad);
        const cy = Math.max(0, minY - pad);
        const cw = Math.min(c.width - cx, maxX - minX + 1 + pad * 2);
        const ch = Math.min(c.height - cy, maxY - minY + 1 + pad * 2);

        const out = document.createElement("canvas");
        out.width = cw;
        out.height = ch;
        out.getContext("2d").drawImage(c, cx, cy, cw, ch, 0, 0, cw, ch);
        return { dataUrl: out.toDataURL("image/png"), aspect: cw / ch };
    } catch {
        return null;
    }
}

/** Draw the shared certificate and save the PDF. */
async function baseCertificate({ eyebrow, recipient, intro, summary, chip, fields, verifyPath, recordLabel, fileId }) {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
    const W = doc.internal.pageSize.getWidth(); // 595.28
    const H = doc.internal.pageSize.getHeight(); // 841.89
    const mid = W / 2;

    /* ---- paper + soft tinted corners (they bleed off the page edges) ---- */
    doc.setFillColor(...PAPER);
    doc.rect(0, 0, W, H, "F");
    doc.setFillColor(...SOFT_BLUE);
    doc.circle(mid + 278, -15, 130, "F");
    doc.setFillColor(...SOFT_GREEN);
    doc.circle(-15, 10, 110, "F");

    /* ---- rounded double border ---- */
    doc.setDrawColor(...BLUE);
    doc.setLineWidth(1.4);
    doc.roundedRect(24, 24, W - 48, H - 48, 16, 16, "S");
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.6);
    doc.roundedRect(31, 31, W - 62, H - 62, 12, 12, "S");

    /* ---- logo ---- */
    const logo = await loadImage("/seedh-logo-email.png");
    if (logo) {
        const h = 88;
        const w = h * (logo.naturalWidth / logo.naturalHeight || 1);
        doc.addImage(logo, "PNG", mid - w / 2, 44, w, h, undefined, "SLOW");
    }

    /* ---- registered name + accent bar ---- */
    doc.setTextColor(...BROWN);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(SEEDH.legalName.toUpperCase(), mid, logo ? 145 : 96, { align: "center" });
    doc.setFillColor(...GREEN);
    doc.roundedRect(mid - 44, (logo ? 152.5 : 103.5), 88, 3.5, 1.75, 1.75, "F");

    /* ---- title + cause line ---- */
    doc.setTextColor(...BLUE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(25);
    doc.text("Certificate of Contribution", mid, 206, { align: "center" });

    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.text(doc.splitTextToSize(eyebrow, W - 150), mid, 226, { align: "center" });

    /* ---- recipient ---- */
    doc.setTextColor(...INK);
    doc.setFontSize(11.5);
    doc.text(intro, mid, 272, { align: "center" });

    doc.setTextColor(...DEEP_GREEN);
    doc.setFont("times", "italic");
    doc.setFontSize(31);
    doc.text(recipient || "Anonymous Donor", mid, 313, { align: "center" });

    doc.setDrawColor(...GREEN);
    doc.setLineWidth(1.1);
    doc.line(mid - 120, 325, mid + 120, 325);

    /* ---- what they gave ---- */
    if (summary) {
        doc.setTextColor(...INK);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.6);
        doc.text(doc.splitTextToSize(summary, 430), mid, 376, {
            align: "center",
            lineHeightFactor: 1.42,
        });
    }

    /* ---- id chip ---- */
    doc.setFillColor(...SOFT_GREEN);
    doc.roundedRect(mid - 105, 396, 210, 34, 17, 17, "F");
    doc.setTextColor(...DEEP_GREEN);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text(chip, mid, 418, { align: "center" });

    /* ---- details card: one labelled row per field ---- */
    const cardX = 74;
    const cardW = W - 148;
    const cardY = 462.9;
    const pitch = 41.25;
    const cardH = 25.25 + (fields.length - 1) * pitch + 16;
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.8);
    doc.roundedRect(cardX, cardY, cardW, cardH, 12, 12, "FD");

    fields.forEach((f, i) => {
        const baseline = cardY + 25.25 + i * pitch;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.7);
        doc.setTextColor(...BROWN);
        doc.text(f.label.toUpperCase(), cardX + 20, baseline);

        const isStatus = f.dot;
        doc.setFont("helvetica", isStatus ? "bold" : "normal");
        doc.setFontSize(10.7);
        doc.setTextColor(...INK);
        const [line] = doc.splitTextToSize(String(f.value || "—"), cardW - 190);
        const rightEdge = cardX + cardW - 20;
        doc.text(line, rightEdge, baseline, { align: "right" });
        if (isStatus) {
            const dotX = rightEdge - doc.getTextWidth(line) - 10;
            doc.setFillColor(...(f.dotColor || GREEN));
            doc.circle(dotX, baseline - 3, 3.1, "F");
        }

        if (i < fields.length - 1) {
            doc.setDrawColor(...RULE);
            doc.setLineWidth(0.6);
            doc.line(cardX + 18, baseline + 16, cardX + cardW - 18, baseline + 16);
        }
    });

    /* ---- QR (left) ---- */
    const qr = await qrDataUrl(`${origin()}${verifyPath}`);
    if (qr) {
        const size = 70;
        const qrX = 98;
        const qrY = 679.9;
        doc.addImage(qr, "PNG", qrX, qrY, size, size, undefined, "SLOW");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...NAVY);
        doc.text("SCAN TO VERIFY", qrX + size / 2, 761.9, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(...MUTED);
        doc.text(recordLabel, qrX + size / 2, 772.9, { align: "center" });
    }

    /* ---- signatory (right) ---- */
    const sigCentre = 443.3;
    const sig = await loadSignature(SEEDH.signatory.signatureImage);
    if (sig) {
        // Contain-fit in the space above the rule, whichever way round the crop is.
        const maxW = 128;
        const maxH = 46;
        let w = maxW;
        let h = w / sig.aspect;
        if (h > maxH) {
            h = maxH;
            w = h * sig.aspect;
        }
        doc.addImage(
            sig.dataUrl,
            "PNG",
            sigCentre - w / 2,
            723.9 - h, // sits 6pt above the rule at y=729.9
            w,
            h,
            undefined,
            "SLOW",
        );
    }
    doc.setDrawColor(...MUTED);
    doc.setLineWidth(0.6);
    doc.line(375.3, 729.9, 511.3, 729.9);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...INK);
    doc.text(SEEDH.signatory.name, sigCentre, 742.9, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.8);
    doc.setTextColor(...MUTED);
    doc.text(SEEDH.signatory.title, sigCentre, 754.9, { align: "center" });

    /* ---- footer ---- */
    doc.setTextColor(...BLUE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.2);
    doc.text(SEEDH.legalName, mid, 793.9, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(SEEDH.tagline, mid, 804.9, { align: "center" });

    doc.save(`Seedh_Certificate_${fileId}.pdf`);
}

/** Certificate for one tree (public tree page). */
export async function downloadTreeCertificate(tree) {
    if (!tree?.id) return;
    const species = tree.species || "a sapling";
    const where = tree.location ? ` at ${tree.location}` : "";
    const when = tree.plantedOn ? ` on ${formatDate(tree.plantedOn)}` : "";
    const dead = tree.status === "Dead" || tree.status === "Replaced";

    await baseCertificate({
        eyebrow: `${CAUSE_LABEL.trees.toUpperCase()}  ·  VERIFIED IMPACT`,
        intro: "Presented with gratitude to",
        recipient: tree.sponsor,
        summary: `for planting a ${species} tree${where}${when}. This tree is publicly tracked as ${tree.id} and monitored by our field team.`,
        chip: `TREE ID  ·  ${tree.id}`,
        fields: [
            { label: "Species", value: tree.species },
            { label: "Location", value: tree.location },
            { label: "Planted on", value: formatDate(tree.plantedOn) },
            {
                label: "Current status",
                value: tree.status,
                dot: true,
                dotColor: dead ? [200, 90, 70] : GREEN,
            },
        ],
        verifyPath: `/tree/${encodeURIComponent(tree.id)}`,
        recordLabel: "Public tree record",
        fileId: tree.id,
    });
}

/** Certificate for a whole drive (public drive page). */
export async function downloadDriveCertificate(drive) {
    if (!drive?.id) return;
    const c = drive.causes ?? {};
    const parts = [];
    if (c.trees > 0) parts.push(`${c.trees} tree${c.trees === 1 ? "" : "s"} planted`);
    if (c.foodPackets > 0) parts.push(`${c.foodPackets} food packets distributed`);
    if (c.children > 0) parts.push(`${c.children} children supported`);

    const causeLabels = [];
    if (c.trees > 0) causeLabels.push(CAUSE_LABEL.trees.toUpperCase());
    if (c.foodPackets > 0) causeLabels.push(CAUSE_LABEL.food.toUpperCase());
    if (c.children > 0) causeLabels.push(CAUSE_LABEL.education.toUpperCase());

    await baseCertificate({
        eyebrow: `${causeLabels.join("  ·  ") || "IMPACT DRIVE"}  ·  VERIFIED IMPACT`,
        intro: "Presented with gratitude to",
        recipient: drive.donor,
        summary: parts.length
            ? `for supporting “${drive.title}” — ${parts.join(", ")}. Every record is publicly verifiable.`
            : `for supporting “${drive.title}”, publicly recorded and verifiable.`,
        chip: `DRIVE ID  ·  ${drive.id}`,
        fields: [
            { label: "Drive", value: drive.title },
            { label: "Location", value: drive.location },
            { label: "Date", value: formatDate(drive.date) },
        ],
        verifyPath: `/drive/${encodeURIComponent(drive.id)}`,
        recordLabel: "Public drive record",
        fileId: drive.id,
    });
}
